import * as d3 from "d3";
import {
  addDays,
  addHours,
  addWeeks,
  endOfDay,
  format,
  isEqual,
  startOfWeek,
} from "date-fns";
import endOfWeek from "date-fns/endOfWeek";
import ruLocale from "date-fns/locale/ru";
import startOfDay from "date-fns/startOfDay";
import { useEffect, useRef } from "react";

import Spinner from "components/ui/spinner/Spinner";
import useQueryString from "services/query/useQueryString";
import numberWithCommas from "utils/numberWithCommas";

import Legend from "./Legend";
import styles from "./RectanglesChart.module.sass";

const margin = { top: 50, left: 120, right: 50, bottom: 50 };
const userHeight = 20;

const getTimeDelimiter = (showByTimeInterval) => {
  switch (showByTimeInterval) {
    case "week":
      return d3.timeMonday;
    case "day":
      return d3.timeDay;
    default:
      return d3.timeHour;
  }
};

const getLegendTitle = (showByValue) => {
  const legendTitleParts = {
    value: {
      ban_number: "Банов",
      ban_number_percent: "Банов платящих (%)",
      irrelevant_ban_number_percent: "Банов отмененных сервером (%)",
      unban_number: "Разбанов",
      unban_number_percent: "Разбанов платящих (%)",
      innefective: "Простой (%)",
      picture_number: "Просмотрено картинок",
      mark_number: "Действий",
    },
    time: "в час",
  };

  return `${legendTitleParts.value[showByValue]} ${legendTitleParts.time}`;
};

const RectanglesChart = ({ data, dimensions, loading }) => {
  const { width } = dimensions;

  const tooltipRef = useRef(null);
  const legendRef = useRef(null);
  const svgRef = useRef(null);

  const setOnlyUsers = useQueryString("onlyUsers", [])[1];

  useEffect(() => {
    if (loading) return;

    const from = data.from ? new Date(data.from) : new Date();
    const to = data.to ? new Date(data.to) : new Date();
    const showByTimeInterval = data.showByTimeInterval || "hour";
    const showByValue = data.showByValue || "ban_number";
    const removeTenMinIntervals =
      data.removeTenMinIntervals === undefined
        ? true
        : data.removeTenMinIntervals;
    const removeTwentyMinIntervals =
      data.removeTwentyMinIntervals === undefined
        ? true
        : data.removeTwentyMinIntervals;
    const users = data.users || [];

    const svg = d3.select(svgRef.current);
    const tooltip = d3.select(tooltipRef.current);
    const legend = d3.select(legendRef.current);

    const timeScale = d3
      .scaleTime()
      .domain([from, to])
      .rangeRound([margin.left, width - margin.right])
      .clamp(true);

    const sortedUsers = users
      .map((user) => {
        const { work = [] } = user;
        const workWithHours = work.map((workData) => {
          const { total_interval_number } = workData;
          const workHours = Math.floor((total_interval_number * 10) / 60);
          const workMinutes = (total_interval_number * 10) % 60;
          const workTime = workHours + workMinutes / 60;

          return { ...workData, workTime };
        });
        const { valueSum, hoursSum } = workWithHours.reduce(
          (result, workData) => {
            if (
              removeTwentyMinIntervals &&
              workData.total_interval_number <= 2
            ) {
              return result;
            }

            if (removeTenMinIntervals && workData.total_interval_number <= 1) {
              return result;
            }

            result.valueSum += workData[showByValue];
            result.hoursSum += workData.workTime;

            return result;
          },
          { valueSum: 0, hoursSum: 0 }
        );

        return {
          ...user,
          work: workWithHours,
          mean:
            hoursSum > 0 ? Math.round((valueSum / hoursSum) * 100) / 100 : 0,
        };
      })
      .filter((user) => user.mean > 0)
      .sort((userA, userB) => {
        const { mean: meanA, by_indonesia: isAIndonesia } = userA;
        const { mean: meanB, by_indonesia: isBIndonesia } = userB;

        const sortDirection =
          showByValue === "innefective" ||
          showByValue === "ban_number_percent" ||
          showByValue === "irrelevant_ban_number_percent" ||
          showByValue === "unban_number_percent"
            ? -1
            : 1;

        if (
          (!isAIndonesia && !isBIndonesia) ||
          (isAIndonesia && isBIndonesia)
        ) {
          return sortDirection * (meanB - meanA);
        } else if (isAIndonesia) {
          return sortDirection;
        } else {
          return sortDirection * -1;
        }
      });

    const allValues = sortedUsers.map((user) => user.mean);

    const minValue = d3.min(allValues);
    const maxValue = d3.max(allValues);

    const interpolator = d3.interpolateNumber(minValue, maxValue);

    const steps = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];

    const valueSteps = steps.map(interpolator);

    const colorSteps = [
      "#DB5E3F",
      "#DB5E3F",
      "#DB5E3F",
      "#ECA15B",
      "#F7DB84",
      "#E4F492",
      "#A8D598",
      "#6DB496",
      "#6DB496",
      "#6DB496",
      "#6DB496",
    ];
    const colorStepsReversed = colorSteps.slice().reverse();

    const color = d3.scaleThreshold(
      valueSteps,
      showByValue === "innefective" ||
        showByValue === "ban_number_percent" ||
        showByValue === "irrelevant_ban_number_percent" ||
        showByValue === "unban_number_percent"
        ? colorStepsReversed
        : colorSteps
    );

    // Clear all
    svg.selectAll("*").remove();
    legend.selectAll("*").remove();

    // Init
    svg
      .attr("width", width)
      .attr("height", margin.top + userHeight + sortedUsers.length * userHeight)
      .attr("font-family", "Roboto")
      .attr("font-size", 10);

    const svgBounds = svg.node().getBoundingClientRect();

    if (maxValue > 0) {
      const legendSvg = Legend(color, {
        title: getLegendTitle(showByValue),
        width: Math.min(420, width - margin.left - margin.right),
        tickFormat:
          showByValue === "picture_number"
            ? (d) => {
                return d < 1000 ? Math.round(d) : Math.round(d / 1000) + "К";
              }
            : ",.1f",
      });

      d3.select(legendSvg).attr("transform", `translate(${margin.left}, 0)`);

      legend.node().appendChild(legendSvg);
    }

    // X axis
    const xAxis = svg
      .append("g")
      .attr("transform", `translate(0, ${margin.top})`);

    // Draw axis with marks
    xAxis.call(
      d3.axisTop(timeScale).ticks(getTimeDelimiter(showByTimeInterval).every(1))
    );

    // Increase start of a day marks for hour intervals to show day changes
    xAxis.selectAll("line").attr("y2", (d) => {
      if (showByTimeInterval === "hour" && isEqual(startOfDay(d), d)) {
        return -15;
      }
      return -5;
    });

    // Format text on marks and move day marks text to the center of the hours interval
    xAxis
      .selectAll("text")
      .text((d) => {
        const day = format(d, "dd MMM", {
          locale: ruLocale,
        });

        const hour = format(d, "kk", {
          locale: ruLocale,
        });

        if (showByTimeInterval === "hour") {
          if (isEqual(startOfDay(d), d)) {
            return day;
          }
          return hour;
        }

        return day;
      })
      .attr("transform", (d) => {
        if (showByTimeInterval === "week") {
          const weekWidth =
            Math.min(
              width - margin.right,
              timeScale(endOfWeek(d, { weekStartsOn: 1 }))
            ) - timeScale(startOfWeek(d, { weekStartsOn: 1 }));

          return `translate(${weekWidth / 2}, 0)`;
        } else {
          const dayWidth =
            Math.min(width - margin.right, timeScale(endOfDay(d))) -
            timeScale(startOfDay(d));

          if (showByTimeInterval === "hour") {
            if (isEqual(startOfDay(d), d)) {
              return `translate(${dayWidth / 2}, -20)`;
            }
            return "translate(0, 0)";
          }
          return `translate(${dayWidth / 2}, 0)`;
        }
      });

    // Y axis
    const yAxis = svg
      .append("g")
      .selectAll("g")
      .data(sortedUsers)
      .enter()
      .append("g")
      .attr(
        "transform",
        (_, i) => `translate(0, ${margin.top + userHeight + i * userHeight})`
      );

    const userNameYAxis = yAxis
      .append("g")
      .style("cursor", "pointer")
      .on("click", (_, d) => {
        setOnlyUsers(
          users.filter((user) => user.id !== d.id).map((user) => user.id)
        );
      })
      .on("mouseover", (event, d) => {
        d3.select(event.target.parentNode).classed(styles.removeUser, true);

        tooltip.style("opacity", 1);
        tooltip.html(`<b>Убрать ${d.name}?</b>`);
      })
      .on("mousemove", (event) => {
        const tooltipBounds = tooltip.node().getBoundingClientRect();

        tooltip
          .style(
            "left",
            Math.min(
              svgBounds.width - tooltipBounds.width - 10,
              event.pageX - svgBounds.x + 10
            ) + "px"
          )
          .style(
            "top",
            Math.min(
              svgBounds.height - tooltipBounds.height,
              event.pageY - svgBounds.y + 10
            ) + "px"
          );
      })
      .on("mouseleave", () => {
        d3.select(`.${styles.removeUser}`).classed(styles.removeUser, false);

        tooltip.style("opacity", 0);
      });

    userNameYAxis
      .append("text")
      .text((d) => d.name)
      .attr("x", margin.left - 50)
      .attr("text-anchor", "end");

    userNameYAxis
      .append("text")
      .text((d) =>
        d.role === "server"
          ? ""
          : d.role === "moderator" && d.disrated < 1
          ? "\u00A0\u00A0($)"
          : "\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0"
      )
      .attr("x", margin.left - 35)
      .attr("text-anchor", "end")
      .attr("fill-opacity", (d) => (d.disrated === 0 ? 1 : 0.5));

    userNameYAxis
      .append("text")
      .text((d) => (d.positionInTop ? `[${d.positionInTop}]` : ""))
      .attr("x", margin.left - 35);

    yAxis
      .append("circle")
      .attr("cx", margin.left - 10)
      .attr("cy", -3)
      .attr("r", 5)
      .attr("fill", (d) => color(d.mean))
      .style("cursor", "help")
      .on("mouseover", (_, d) => {
        tooltip.style("opacity", 1);
        tooltip.html(
          `<b>${getLegendTitle(showByValue)}: ${
            showByValue === "picture_number"
              ? d.mean < 1000
                ? Math.round(d.mean)
                : Math.round(d.mean / 1000) + "К"
              : numberWithCommas(d.mean, 1)
          }</b>`
        );
      })
      .on("mousemove", (event) => {
        const tooltipBounds = tooltip.node().getBoundingClientRect();

        tooltip
          .style(
            "left",
            Math.min(
              svgBounds.width - tooltipBounds.width - 10,
              event.pageX - svgBounds.x + 10
            ) + "px"
          )
          .style(
            "top",
            Math.min(
              svgBounds.height - tooltipBounds.height,
              event.pageY - svgBounds.y + 10
            ) + "px"
          );
      })
      .on("mouseleave", () => {
        tooltip.style("opacity", 0);
      });

    // Content
    svg
      .append("g")
      .selectAll("g")
      .data(sortedUsers)
      .enter()
      .append("g")
      .attr(
        "transform",
        (_, i) => `translate(0, ${margin.top + 7 + i * userHeight})`
      )
      .selectAll("rect")
      .data((data) =>
        data.work
          ? data.work.filter(
              (work) =>
                (showByValue === "unban_number" ||
                  showByValue === "ban_number_percent" ||
                  showByValue === "irrelevant_ban_number_percent" ||
                  showByValue === "unban_number_percent" ||
                  showByValue === "innefective" ||
                  (showByValue !== "unban_number" &&
                    showByValue !== "ban_number_percent" &&
                    showByValue !== "irrelevant_ban_number_percent" &&
                    showByValue !== "unban_number_percent" &&
                    showByValue !== "innefective" &&
                    work[showByValue] > 0)) &&
                ((removeTwentyMinIntervals && work.total_interval_number > 2) ||
                  !removeTwentyMinIntervals) &&
                ((removeTenMinIntervals && work.total_interval_number > 1) ||
                  !removeTenMinIntervals)
            )
          : []
      )
      .enter()
      .append("rect")
      .attr(
        "x",
        (d) => Math.max(margin.left, timeScale(new Date(d.timestamp))) + 1
      )
      .attr("width", (d) => {
        const from = new Date(d.timestamp);

        let to;

        if (showByTimeInterval === "week") {
          to = addWeeks(from, 1);
        }
        if (showByTimeInterval === "day") {
          to = addDays(from, 1);
        }
        if (showByTimeInterval === "hour") {
          to = addHours(from, 1);
        }

        const rectWidth = Math.max(
          0,
          Math.min(timeScale(to), width - margin.right) -
            Math.max(margin.left, timeScale(from)) -
            2
        );

        return rectWidth;
      })
      .attr("height", 18)
      .attr("fill", (d) => {
        const { workTime } = d;

        return workTime > 0 &&
          (showByValue === "innefective" ||
          showByValue === "ban_number_percent" ||
          showByValue === "irrelevant_ban_number_percent" ||
          showByValue === "unban_number_percent"
            ? true
            : d[showByValue] > 0)
          ? color(d[showByValue] / workTime)
          : "#DCDCDC";
      })
      .on("mouseover", (event) => {
        tooltip.style("opacity", 1);

        const targetBounds = event.target.getBBox();
        const { f: targetParentY } =
          event.target.parentNode.transform.baseVal.consolidate().matrix;

        d3.select(event.target)
          .style("stroke", "black")
          .style("cursor", "help");

        svg
          .append("rect")
          .attr("x", targetBounds.x)
          .attr("y", 0)
          .attr("width", targetBounds.width)
          .attr("height", svgBounds.height)
          .attr("fill", "#FF0000")
          .attr("fill-opacity", 0.1)
          .attr("pointer-events", "none")
          .classed("verticalHelper", true);

        svg
          .append("rect")
          .attr("x", 0)
          .attr("y", targetParentY)
          .attr("width", svgBounds.width)
          .attr("height", targetBounds.height)
          .attr("fill", "#FF0000")
          .attr("fill-opacity", 0.1)
          .attr("pointer-events", "none")
          .classed("horizontalHelper", true);
      })
      .on("mousemove", (event, d) => {
        const {
          moderator_id,
          ban_number,
          unban_number,
          payer_ban_number,
          irrelevant_ban_number,
          payer_unban_number,
          picture_number,
          mark_number,
          total_interval_number,
          innefective,
          timestamp,
          workTime,
        } = d;
        const user = sortedUsers.find((user) => user.id === moderator_id);

        const workHours = Math.floor((total_interval_number * 10) / 60);
        const workMinutes = (total_interval_number * 10) % 60;

        const meanValue = d[showByValue] / workTime;

        const meanInfo = `<b>${getLegendTitle(showByValue)}: ${
          workTime > 0
            ? showByValue === "picture_number"
              ? meanValue < 1000
                ? meanValue
                : Math.round(meanValue / 1000) + "К"
              : numberWithCommas(Math.round(meanValue * 100) / 100, 1)
            : "-"
        }</b>`;
        const meanBanInfo = `<b>${getLegendTitle("ban_number")}: ${
          workTime > 0
            ? numberWithCommas(
                Math.round((d.ban_number / workTime) * 100) / 100,
                1
              )
            : "-"
        }</b>`;
        const meanUnbanInfo = `<b>${getLegendTitle("unban_number")}: ${
          workTime > 0
            ? numberWithCommas(
                Math.round((d.unban_number / workTime) * 100) / 100,
                1
              )
            : "-"
        }</b>`;

        const showMeanBan = showByValue === "unban_number";
        const showMeanUnban = showByValue === "ban_number";
        const showBoth = showByValue === "picture_number";

        const banInfo = `- баны: ${numberWithCommas(ban_number)}`;
        const unbanInfo = `- разбаны: ${numberWithCommas(unban_number)}`;
        const payersBanInfo = `- баны плательщиков: ${numberWithCommas(
          payer_ban_number
        )}`;
        const irrelevantBanInfo = `- баны отмененные сервером: ${numberWithCommas(
          irrelevant_ban_number
        )}`;
        const payersUnbanInfo = `- разбаны плательщиков: ${numberWithCommas(
          payer_unban_number
        )}`;
        const picturesInfo = `- просмотрено картинок: ${numberWithCommas(
          picture_number
        )}`;
        const markInfo = `- действий: ${numberWithCommas(mark_number)}`;
        const innefectiveInfo = `- простой: ${innefective}%`;
        const timeInfo = `- время работы: ${workHours} ч. ${workMinutes} м.`;

        const date = new Date(timestamp);

        const html = `<b>${user.name}</b> ${format(date, "dd MMM hh:mm", {
          locale: ruLocale,
          weekStartsOn: 1,
        })}:
        <br>
        ${meanInfo}
        <br>
        ${
          showMeanBan
            ? meanBanInfo + "<br>"
            : showMeanUnban
            ? meanUnbanInfo + "<br>"
            : showBoth
            ? meanUnbanInfo + "<br>" + meanBanInfo + "<br>"
            : ""
        }
        ${banInfo}
        <br>
        ${payersBanInfo}
        <br>
        ${irrelevantBanInfo}
        <br>
        ${unbanInfo}
        <br>
        ${payersUnbanInfo}
        <br>
        ${picturesInfo}
        <br>
        ${
          user.role === "supermoderator" || user.role === "admin"
            ? markInfo + "<br>"
            : ""
        }
        ${innefectiveInfo}
        <br>
        ${timeInfo}`;

        const tooltipBounds = tooltip.node().getBoundingClientRect();

        tooltip
          .html(html)
          .style(
            "left",
            Math.min(
              svgBounds.width - tooltipBounds.width - 10,
              event.pageX - svgBounds.x + 10
            ) + "px"
          )
          .style(
            "top",
            Math.min(
              svgBounds.height - tooltipBounds.height,
              event.pageY - svgBounds.y + 10
            ) + "px"
          );
      })
      .on("mouseleave", (event) => {
        tooltip.style("opacity", 0);

        svg.select(".verticalHelper").remove();
        svg.select(".horizontalHelper").remove();

        d3.select(event.target).style("stroke", "none").style("cursor", "auto");
      });
  }, [loading, width, data, setOnlyUsers]);

  return (
    <div className={styles.container}>
      {loading ? (
        <Spinner />
      ) : (
        <>
          <div ref={tooltipRef} className={styles.tooltip}></div>
          <div ref={legendRef}></div>
          <svg ref={svgRef} />
        </>
      )}
    </div>
  );
};

export default RectanglesChart;
